import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { GiTempleGate } from "react-icons/gi";
import api from "../utils/api";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";

export default function OtherServiceList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = () => {
    setLoading(true);
    api
      .get("/other-services/admin/all")
      .then((res) => setItems(res.data))
      .catch(() => toast.error("Failed to load other services"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/other-services/${id}`);
      toast.success("Service deleted");
      setItems(items.filter((s) => s._id !== id));
    } catch (err) {
      toast.error("Failed to delete service");
    }
  };

  const togglePublished = async (item) => {
    try {
      const { data } = await api.put(`/other-services/${item._id}`, { published: !item.published });
      setItems(items.map((s) => (s._id === item._id ? data : s)));
      toast.success(data.published ? "Published" : "Unpublished");
    } catch (err) {
      toast.error("Failed to update service");
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl text-maroon-800">Other Pooja Services</h1>
          <p className="text-sm text-gray-500 mt-1">Manage the "Also Available" services list shown on the Pooja Services page.</p>
        </div>
        <Link
          to="/admin/other-services/new"
          className="inline-flex items-center gap-2 bg-saffron-500 hover:bg-saffron-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
        >
          <FiPlus /> New Service
        </Link>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No services yet" subtitle="Click 'New Service' to add items to the list." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <div key={s._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-saffron-100 to-gold-50 flex items-center justify-center text-saffron-600 shrink-0">
                  <GiTempleGate size={22} />
                </div>
                <button
                  onClick={() => togglePublished(s)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    s.published ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                  }`}
                >
                  {s.published ? "Published" : "Draft"}
                </button>
              </div>
              <h3 className="font-heading text-maroon-800 text-sm leading-snug mb-1">{s.title}</h3>
              {s.desc && <p className="text-xs text-gray-500 line-clamp-2 mb-3 flex-1">{s.desc}</p>}
              <div className="flex items-center justify-between border-t border-gray-100 pt-3 mt-auto">
                <span className="text-xs text-gray-400">Order: {s.order}</span>
                <div className="flex items-center gap-2">
                  <Link to={`/admin/other-services/edit/${s._id}`} className="p-2 rounded-lg hover:bg-gray-100 text-saffron-600">
                    <FiEdit2 />
                  </Link>
                  <button onClick={() => handleDelete(s._id, s.title)} className="p-2 rounded-lg hover:bg-gray-100 text-red-500">
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
